import React, { useState, memo } from 'react'
import ReactApexChart from 'react-apexcharts'
import Button from '@material-ui/core/Button'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import { makeStyles } from '@material-ui/core/styles'

import * as optionsChart from 'components/ChartArea/chartOptions'
import FacilityItem from './FacilityItem'
import ExpandButton from './ExpandButton'
import { useShallowEqualSelector } from 'custom-hooks'
import { getGroupDataLocationsSelector } from 'redux/reducers/overview'
import LoaderCharts from './LoaderCharts'
import NoDataPerson from 'components/CommonUI/svgIcons/NoDataPerson'

const CONSUMPTION = 'consumption'
const ENERGY_INTENSITY = 'intensity'

const useStyles = makeStyles(() => ({
  root: {
    border: 'none',
    height: 'inherit',
    width: '100%',
    overflow: 'hidden',
    borderRadius: '9px 9px 0 0',
  },
}))

const useStylesByttonsMenu = makeStyles(() => ({
  root: {
    border: 'none',
    height: 'inherit',
    backgroundColor: '#eaebed',
    borderRadius: 0,
  },
  disabled: {
    border: 'none !important',
  },

  label: {
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '20px',
    letterSpacing: '0.01em',
    color: '#838992',
    textTransform: 'none',
  },
}))

const BarChart = ({ isFetching, className = '', setHovetFacility }) => {
  const classes = useStyles()
  const classesButtonMenu = useStylesByttonsMenu()

  const [selectMetric, setSelectMetrics] = useState(CONSUMPTION)
  const [isExpand, setIsExpand] = useState(false)

  const locations = useShallowEqualSelector(getGroupDataLocationsSelector)

  const consumptionUnit = 'kWh'
  const intensityUnit = `<span>
                            kWh/m<sup>2</sup>
                         </span>`

  const unitName =
    selectMetric === CONSUMPTION ? consumptionUnit : intensityUnit

  const seriesData =
    selectMetric === CONSUMPTION
      ? locations?.seriesConsaption
      : locations?.seriesIntencity

  const _series = [{ data: seriesData, color: '#4AA652', name: unitName }]

  const handleClickSelect = (value) => () => {
    setSelectMetrics(value)
  }

  const handleClickExpand = () => {
    setIsExpand((prev) => !prev)
  }

  const options = {
    chart: {
      ...optionsChart.chart,
      events: {
        dataPointMouseEnter: function (event, chartContext, config) {
          const item = locations?.items[config.dataPointIndex]
          setHovetFacility(item.location_id)
        },
        dataPointMouseLeave: function () {
          setHovetFacility('')
        },
      },
    },
    plotOptions: {
      bar: optionsChart.plotOptions.bar,
    },
    dataLabels: optionsChart.dataLabels,
    xaxis: {
      show: true,
      categories: locations?.categories,
      position: 'top',
      axisBorder: {
        show: false,
      },
    },
    yaxis: {
      show: false,
    },
    grid: {
      show: true,
      borderColor: '#d8dbdf',
      position: 'back',
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'darken',
          value: 1,
        },
      },
      hover: {
        filter: {
          type: 'darken',
          value: 1,
        },
      },
      active: {
        filter: {
          type: 'none',
        },
      },
    },
    tooltip: {
      enabled: true,
      followCursor: true,
      custom: function (props) {
        const { w, dataPointIndex, seriesIndex, series } = props
        return `<div class='tooltip_container'>
                <span class='yaxis_text'>
                    ${series[seriesIndex][dataPointIndex]} 
                    <span>${w.globals.seriesNames[seriesIndex]}</span>
                </span>
            </div>`
      },
    },
  }

  const classNameExpand = isExpand ? 'expand' : ''

  const classNameNodata = seriesData?.length ? '' : 'no_data'

  const classNameIsFetching = isFetching ? 'loading' : ''

  return (
    <div
      className={`bar_charts_container ${className} ${classNameExpand} ${classNameIsFetching}`}
    >
      {isFetching && <LoaderCharts />}
      {locations?.items?.length > 3 && (
        <ExpandButton onClick={handleClickExpand} />
      )}
      <div className="bar_charts_header">
        <div className="navigation">
          <ButtonGroup classes={classes} fullWidth>
            <Button
              classes={classesButtonMenu}
              className={`consumption ${
                selectMetric === CONSUMPTION ? 'active' : ''
              }`}
              onClick={handleClickSelect(CONSUMPTION)}
              disabled={isFetching}
            >
              Consumption
            </Button>
            <Button
              classes={classesButtonMenu}
              className={`energy_intensity ${
                selectMetric === ENERGY_INTENSITY ? 'active' : ''
              }`}
              onClick={handleClickSelect(ENERGY_INTENSITY)}
              disabled={isFetching}
            >
              Energy intensity
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {!isFetching && (
        <div className="bar_charts_body">
          {seriesData?.length ? (
            <>
              <div className="facilitys_list">
                {locations?.items?.map((category, i) => (
                  <FacilityItem
                    key={category?.location_id}
                    data={category}
                    index={i}
                  />
                ))}
              </div>
              <div className="charts_container">
                <div className={`chart_wrap ${classNameNodata}`}>
                  <ReactApexChart
                    options={options}
                    series={_series}
                    type="bar"
                    height={63 + 94 * locations?.categories?.length}
                    width={'99%'}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="no_data">
              <div className="text_content_wrap">
                <span className="no_data_text">No Data</span>
                <span className="no_data_text2">
                  This chart is currently empty
                  <NoDataPerson className="no_data_icon" />
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default memo(BarChart)
